import { useAuthStore } from '@/core/stores/auth'

type Role = 'admin' | 'manager' | 'viewer'

interface RequireRoleProps {
  roles: Role[]
  children: React.ReactNode
  fallback?: React.ReactNode
}

/**
 * Renders children only if the current user has one of the allowed roles.
 * Use for conditional UI elements (buttons, sections).
 * For route-level protection, use in the Route element.
 */
export function RequireRole({ roles, children, fallback = null }: RequireRoleProps) {
  const role = useAuthStore((s) => s.user?.role)

  if (!role || !roles.includes(role as Role)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * Hook version for programmatic checks.
 */
export function useHasRole(...roles: Role[]): boolean {
  const role = useAuthStore((s) => s.user?.role)
  return !!role && roles.includes(role as Role)
}

/**
 * Quick helpers.
 */
export const useIsAdmin = () => useHasRole('admin')
export const useCanEdit = () => useHasRole('admin', 'manager')
export const useIsViewer = () => useHasRole('viewer')
